import { FC, useState } from 'react';
import { Link } from 'react-router-dom';

import { useMailSequenceSaveTemplateMutation } from '../../../generated/graphql';
import Button from '../../components/Button';
import InputGroup from '../../components/InputGroup';
import Modal from '../../components/Modal';
import { customToast } from '../../components/ToastAlert/customToast';
import { SavedActivityInputPreview } from '../../lib/followUp';
import {
  TemplateStepDraft,
  StepActivitiesState,
  StepResourceModeState,
  buildEnrollStepActivitiesFromState,
} from '../../lib/mailSequence';
import { toEnrollmentActivityInputs } from './helpers';
import { MAIL_SEQUENCE_ALERT_LINK_CLASS } from './scheduleMailFieldLabel';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  templateId?: string;
  initialName?: string;
  steps: TemplateStepDraft[];
  defaultActivities: SavedActivityInputPreview[];
  stepActivitiesState: StepActivitiesState;
  stepResourceModes: StepResourceModeState;
  stepSourceTemplateIds: Record<number, string>;
  onSaved: (templateId: string) => void;
}

const SaveTemplateModal: FC<Props> = ({
  isOpen,
  onClose,
  templateId,
  initialName = '',
  steps,
  defaultActivities,
  stepActivitiesState,
  stepResourceModes,
  stepSourceTemplateIds,
  onSaved,
}) => {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  const [saveTemplate, { loading }] = useMailSequenceSaveTemplateMutation({
    onCompleted: ({ mailSequenceSaveTemplate }) => {
      if (
        mailSequenceSaveTemplate?.success &&
        mailSequenceSaveTemplate.template
      ) {
        customToast.success('Template saved.');
        onSaved(mailSequenceSaveTemplate.template.id);
        onClose();
      } else {
        setError(mailSequenceSaveTemplate?.error || 'Could not save template.');
      }
    },
    onError: (err) => setError(err.message || 'Could not save template.'),
  });

  const handleSave = () => {
    if (!name.trim()) {
      setError('Template name is required.');
      return;
    }
    setError(null);

    const defaultActivityInputs = toEnrollmentActivityInputs(defaultActivities);

    saveTemplate({
      variables: {
        input: {
          templateId: templateId || undefined,
          name: name.trim(),
          defaultActivities: defaultActivityInputs.length
            ? defaultActivityInputs
            : undefined,
          steps: steps.map((s, i) => {
            const stepActivities = buildEnrollStepActivitiesFromState(
              stepActivitiesState,
              stepResourceModes,
              i,
              defaultActivities,
              toEnrollmentActivityInputs,
            );
            return {
              offsetDays: s.offsetDays,
              subject: s.subject.trim(),
              providerNote: s.providerNote,
              ...(stepActivities ? { activities: stepActivities } : {}),
              ...(stepSourceTemplateIds[i]
                ? { sourceFollowUpTemplateId: stepSourceTemplateIds[i] }
                : {}),
            };
          }),
        },
      },
    });
  };

  return (
    <Modal isOpen={isOpen} setClosed={onClose} width="medium">
      <h2 className="text-subtitle font-semibold text-neutral-150">
        Save as template
      </h2>
      <div className="mt-4 space-y-4">
        <p className="text-small-caption text-neutral-110">
          Save these steps, resources, and per-step customizations to reuse
          later from{' '}
          <Link
            to="/automations/templates/mail-sequence"
            className={MAIL_SEQUENCE_ALERT_LINK_CLASS}
          >
            Automations → Templates → Mail Sequence Templates
          </Link>
          .
        </p>
        <InputGroup
          label="Template name"
          inputSize="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Post-retreat follow-up"
        />
        {error && <p className="text-small-caption text-red-150">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button title="Cancel" theme="secondary" onClick={onClose} />
          <Button
            title={templateId ? 'Update template' : 'Save template'}
            theme="primary"
            onClick={handleSave}
            disabled={loading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SaveTemplateModal;
